// Used to download masters

// Exports an object containing different masters
export const masterConfigs = {
  // location master
  location: {
    // headers for the location master download
    headers: [
      "Sr No.",
      "Location Code",
      "Location*",
      "District",
      "State*",
      "Pincode",
      "Address",
      "Location Type",
      "Customer Name",
      "GST No",
      "Opening Time",
      "Closing Time",
    ],
    // Function to map location data to their table rows
    mapData: (item, index) => [
      index + 1,
      item?.location_code?.toString() ?? "",
      item?.location?.location_name ?? "",
      item?.location?.location?.district ?? "",
      item?.location?.location?.state ?? "",
      item?.pincode ?? "",
      item?.addressLine1 ?? "",
      item?.type ?? "",
      item?.customer_name ?? "",
      item?.gst_no?.toString() ?? "",
      item?.location_timings?.[0]?.open,
      item?.location_timings?.[0]?.close,
    ],
  },

  // route master
  route: {
    // headers for route master download file
    headers: [
      "Sr No.",
      "Origin Code",
      "Origin*",
      "Origin District",
      "Origin State*",
      "Origin Pincode",
      "Destination Code",
      "Destination*",
      "Destination District",
      "Destination State*",
      "Destination Pincode",
      "Route Type*",
      "Distance",
      "Duration",
      "Spot Route*",
      "Route Code*",
      "No of hops",
      "Sort Order",
    ],
    // Function to map route master data to table rows
    mapData: (item, index) => [
      index + 1,
      item?.origin?.location_code?.toString() ?? "",
      item?.origin?.location?.label ?? "",
      item?.origin?.location?.district ?? "",
      item?.origin?.location?.state ?? "",
      item?.origin.pincode ?? "",
      item?.destination?.location_code?.toString() ?? "",
      item?.destination?.location?.label ?? "",
      item?.destination?.location?.district ?? "",
      item?.destination?.location?.state ?? "",
      item?.destination.pincode ?? "",
      item.route_type,
      item.distance ?? "",
      item.duration ?? "",
      item.spot_route ? "Yes" : "No",
      item.route_code ?? "",
      item.no_of_hops ?? "",
      item.sort_order ?? "",
    ],
  },

  // material master
  material: {
    // headers for the material master download
    headers: [
      "Sr No.",
      "Material Description",
      "Material Type",
      "Material Category",
      "Material Code",
      "Length",
      "Breadth",
      "Height",
      "Volume",
      "Weight",
    ],
    // Function to map material data to table rows
    mapData: (item, index) => [
      index + 1,
      item?.material_description ?? "",
      item?.material_type ?? "",
      item?.material_category ?? "",
      item?.material_code ?? "",
      item?.length ?? "",
      item?.breadth ?? "",
      item?.height ?? "",
      item?.volume ?? "",
      item?.weight ?? "",
    ],
  },

  // vendor master
  vendor: {
    // headers for the vendor master download table
    headers: ["Sr No.", "Transporter Name", "Transporter Code", "Credit Term"],
    // Function to map vendor data to table rows
    mapData: (item, index) => [
      index + 1,
      item?.transporter_name ?? "",
      item?.vendor_code ?? "",
      item?.credit_term ?? "",
    ],
  },

  // truck master
  truck: {
    // Function to generate headers for the truck download file 
    getHeaders: (data) => {
      // Calculate maximum number of truck names (numbers) column 
      const maxTrucks = Math.max(
        ...data.map((item) => item.trucks?.length ?? 0)
      );
      // Generate headers for each truck
      const truckHeaders = Array.from(
        { length: maxTrucks },
        (_, i) => `Vehicle ${i + 1}`
      );

      // Return the complete list of headers
      return [
        "Sr No.",
        "Truck Category*",
        "Truck Name",
        "Truck Type*",
        "Length*",
        "Breadth*",
        "Height*",
        "Volume",
        "Radius",
        "Weight*",
        "Weight Unit*",
        "Number of Trucks",
        ...truckHeaders,
      ];
    },
    // Function to map truck data to table rows
    mapData: (item, index, maxTrucks) => {
      // Extract truck numbers and pad with empty strings if empty
      const truckNumbers = item.trucks?.map((truck) => truck.vehicle_no) ?? [];
      const truckData = [
        ...truckNumbers,
        ...Array(maxTrucks - truckNumbers.length).fill(""),
      ];

      // Return the complete truck row data
      return [
        index + 1,
        item.truck_category ?? "",
        item.truck_name ?? "",
        item.truck_type ?? "",
        item.dimensions?.length ?? "",
        item.dimensions?.breadth ?? "",
        item.dimensions?.height ?? "",
        item.dimensions?.volume ?? "",
        item.radius ?? "",
        item.weight ?? "",
        item.unit ?? "",
        item.no_of_trucks ?? "",
        ...truckData,
      ];
    },
  },

  // freight master
  freight: {
    // Function to generate headers for the freight master download table
    getHeaders: (data) => {
      // Calculate the maximum number of transporters for any lane
      const maxTransporters = Math.max(
        ...data.map((item) => item.transporter_details?.length ?? 0)
      );
      // Generate headers for each transporter
      const transporterHeaders = Array.from(
        { length: maxTransporters },
        (_, i) => [
          `L${i + 1} Transporter Freight Rate*`,
          `L${i + 1} Transporter Name*`,
          `L${i + 1} Transporter Share of business (%)`,
        ]
      ).flat();

      // Return the complete list of headers
      return [
        "S No.",
        "Origin Name*",
        "Origin District",
        "Origin State*",
        "Origin ID",
        "Origin Address",
        "Destination Name*",
        "Destination District",
        "Destination State*",
        "Destination ID",
        "Destination Address",
        "Truck Type*",
        "Distance (Km)",
        "Transit Time (Days)",
        "Annual Volume*",
        "Type of Good",
        "Unit",
        "Baseline",
        "Market Rate",
        "Site Rate",
        "Challenge Price",
        "Zone",
        "Route Code",
        ...transporterHeaders,
      ];
    },
    // Function to map freight data to columns in excel
    mapData: (item, index, maxTransporters) => {
      // Extract transporter data and pad with empty strings if empty
      const onlyName = (value = "") => value.split(",")[0].trim();
      const transporterData =
        item.transporter_details
          ?.map((t) => [
            t.rate ?? "",
            t.transporter?.transporter_name ?? "",
            t.business_share ?? "",
          ])
          .flat() ?? [];

      const paddedTransporterData = [
        ...transporterData,
        ...Array(maxTransporters * 3 - transporterData.length).fill(""),
      ];

      // Return the lane data 
      const originLabel = item?.origin?.location?.label ?? "";
      const destinationLabel = item?.destination?.location?.label ?? "";

      return [
        index + 1,
        onlyName(originLabel),
        item?.origin?.location?.district ?? "",
        item?.origin?.location?.state ?? "",
        item?.origin?.location_code?.toString() ?? "",
        item?.origin?.location_name !== item?.origin?.location?.label
          ? item?.origin?.location_name
          : "",
        onlyName(destinationLabel),
        item?.destination?.location?.district ?? "",
        item?.destination?.location?.state ?? "",
        item?.destination?.location_code?.toString() ?? "",
        item?.destination?.location_name !== item?.destination?.location?.label
          ? item?.destination?.location_name
          : "",
        item.truck_type?.value ?? "",
        item.distance ?? "",
        item.transit_time ?? "",
        item.volume ?? "",
        item.good_type ?? "",
        item.unit ?? "",
        item?.baseline_rate ?? "",
        item?.market_rate ?? "",
        item?.site_rate ?? "",
        item?.challenge_price ?? "",
        item.zone ?? "",
        item.route_code ?? "",
        ...paddedTransporterData,
      ];
    },
  },
};
